import { Loader2, ShieldAlert, Sparkles, Star } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { previewReviewResponseAgentPath } from "@/routes"

import type { ExampleReview } from "./types"

interface CannedReview {
  id: string
  rating: number
  author: string
  text: string
}

// Human-readable reasoning shown on each example card in place of a filler
// reviewer name — it tells the client what scenario the agent is being tested
// against. Keyed by the example's scenario (CannedReview.id).
const SCENARIO_LABELS: Record<string, string> = {
  false_claim: "False claim",
  allegation: "Serious allegation",
  another_language: "Non-English review",
  angry_legit: "Angry but legitimate",
  mixed: "Mixed review",
  happy: "Praise",
}

// General fallback covering the same scenario set as the industry-themed
// examples (IMP-305): false claim, allegation, another language, angry but
// legitimate, mixed, happy.
const FALLBACK_REVIEWS: CannedReview[] = [
  {
    id: "false_claim",
    rating: 1,
    author: "Jordan P.",
    text: "They were closed when I showed up, even though the sign said open. Total waste of my time.",
  },
  {
    id: "allegation",
    rating: 1,
    author: "Casey L.",
    text: "The staff were completely unprofessional and I felt discriminated against. Disgraceful.",
  },
  {
    id: "another_language",
    rating: 5,
    author: "Lucía R.",
    text: "¡Una experiencia maravillosa! El personal fue muy amable y todo estaba impecable.",
  },
  {
    id: "angry_legit",
    rating: 2,
    author: "Sarah M.",
    text: "Waited over 20 minutes with no acknowledgement. Staff seemed completely unbothered. Very disappointing.",
  },
  {
    id: "mixed",
    rating: 3,
    author: "James T.",
    text: "Good enough but the wait was longer than expected. The result was fine. Nothing special either way.",
  },
  {
    id: "happy",
    rating: 5,
    author: "Rachel K.",
    text: "Absolutely fantastic from start to finish — professional, friendly, and quick. Will definitely be back!",
  },
]

type PreviewResult =
  | { kind: "response"; text: string }
  | { kind: "flagged"; text: string; reason: string; note: string }

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            "size-3",
            i < rating
              ? "fill-amber-400 text-amber-400"
              : "fill-muted text-muted",
          )}
        />
      ))}
    </span>
  )
}

export function ReviewPreview({
  agentUid,
  exampleReviews,
}: {
  agentUid: string
  exampleReviews: ExampleReview[] | null
}) {
  // IMP-305: industry-themed reviews when generated, else the general set.
  const reviews: CannedReview[] =
    exampleReviews && exampleReviews.length > 0
      ? exampleReviews.map((r) => ({
          id: r.scenario,
          rating: r.rating,
          author: r.author,
          text: r.text,
        }))
      : FALLBACK_REVIEWS
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCustom, setShowCustom] = useState(false)
  const [customText, setCustomText] = useState("")
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<PreviewResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const activeReview = showCustom
    ? null
    : reviews.find((r) => r.id === selectedId)
  const canGenerate = showCustom
    ? customText.trim().length > 0
    : selectedId !== null

  function clearResult() {
    setResult(null)
    setError(null)
  }

  function selectCanned(id: string) {
    setShowCustom(false)
    setSelectedId(id)
    clearResult()
  }

  function chooseCustom() {
    setShowCustom(true)
    setSelectedId(null)
    clearResult()
  }

  async function generate() {
    setGenerating(true)
    setResult(null)
    setError(null)

    const payload = showCustom
      ? { text: customText.trim() }
      : {
          text: activeReview!.text,
          rating: activeReview!.rating,
          reviewer_name: activeReview!.author,
        }

    try {
      const csrf = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )?.content
      const res = await fetch(
        previewReviewResponseAgentPath({ id: agentUid }),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrf ?? "",
          },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        },
      )
      const body = (await res.json()) as {
        flagged?: boolean
        response?: string
        reason?: string
        note?: string
        error?: string
      }
      if (!res.ok) {
        throw new Error(body.error ?? "Couldn't generate a preview.")
      }
      if (body.flagged) {
        setResult({
          kind: "flagged",
          text: body.response ?? "",
          reason: body.reason ?? "",
          note: body.note ?? "",
        })
      } else {
        setResult({ kind: "response", text: body.response ?? "" })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't generate a preview.")
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <Label>Preview a response</Label>
        <p className="text-muted-foreground mt-0.5 text-sm">
          See how the agent would reply using your settings. Nothing is posted.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {reviews.map((review) => (
          <button
            key={review.id}
            type="button"
            onClick={() => selectCanned(review.id)}
            className={cn(
              "rounded-lg border p-3 text-left transition-colors",
              !showCustom && selectedId === review.id
                ? "border-blue-300 bg-blue-50 dark:bg-blue-950/40"
                : "border-border hover:bg-muted/50",
            )}
          >
            <StarRating rating={review.rating} />
            <p className="text-muted-foreground mt-1.5 line-clamp-3 text-xs leading-relaxed">
              {review.text}
            </p>
            <p className="text-muted-foreground mt-1.5 text-xs font-medium">
              {SCENARIO_LABELS[review.id] ?? "Example review"}
            </p>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={chooseCustom}
        className={cn(
          "w-full rounded-lg border border-dashed p-3 text-left transition-colors",
          showCustom
            ? "border-blue-300 bg-blue-50 dark:bg-blue-950/40"
            : "border-border hover:bg-muted/50",
        )}
      >
        {showCustom ? (
          <textarea
            autoFocus
            value={customText}
            onChange={(e) => {
              setCustomText(e.target.value)
              clearResult()
            }}
            onClick={(e) => e.stopPropagation()}
            placeholder="Write a review here…"
            rows={3}
            className="placeholder:text-muted-foreground w-full resize-none bg-transparent text-xs outline-none"
          />
        ) : (
          <span className="text-muted-foreground text-xs">
            Or write your own review…
          </span>
        )}
      </button>

      <Button
        variant="outline"
        size="sm"
        disabled={!canGenerate || generating}
        onClick={() => void generate()}
        className="gap-1.5"
      >
        {generating ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Sparkles className="size-3.5" />
        )}
        {generating ? "Generating…" : "Generate response"}
      </Button>

      {error && (
        <p className="text-destructive text-sm">{error} Please try again.</p>
      )}

      {result?.kind === "response" && (
        <div className="bg-muted/40 space-y-2 rounded-lg border p-4">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Agent response
          </p>
          <p className="text-sm leading-relaxed whitespace-pre-line">
            {result.text}
          </p>
        </div>
      )}

      {result?.kind === "flagged" && (
        <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 dark:text-amber-400">
            <ShieldAlert className="size-4" />
            Flagged — held for your approval
          </p>
          {result.reason && (
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {result.reason}
            </p>
          )}
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              Agent response
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-line">
              {result.text}
            </p>
          </div>
          <p className="text-muted-foreground text-xs">{result.note}</p>
        </div>
      )}
    </div>
  )
}
